import {
    useMemo,
    useRef,
} from 'react';

import {
    Alert,
    Stack,
} from '@mantine/core';
import {useNavigate, useParams} from 'react-router-dom';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableColumn} from "mantine-datatable";

import {useSteps} from '../hooks/useSteps.ts';
import {useStepsSelection} from '../hooks/useStepsSelection.ts';
import {useStepsSearch} from "../hooks/useStepsSearch.ts";
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {rbacActions} from "../../../config/rbac/actions.ts";
import {Step} from '../types.ts';

export const RecordsTable = () => {

    const {allowedActions} = useVerifiedPermissions()

    const navigate = useNavigate();

    // Scope the list query to the current phase (preferred) or playbook, from
    // the route params. IndexPage is always rendered inside a phase route, so
    // phaseId is the expected scope. playbookId is included as a fallback.
    //
    // This object goes into the queryKey via stepsKeys.list — moving from one
    // phase to another therefore fetches rather than serving the previous
    // phase's cached steps.
    const {id: playbookId, phaseId} = useParams();
    const filters = useMemo<Record<string, string>>(() => {
        const next: Record<string, string> = {};
        if (phaseId) {
            next.phaseId = phaseId;
        } else if (playbookId) {
            next.playbookId = playbookId;
        }
        return next;
    }, [phaseId, playbookId]);

    const {
        columns,
        // The full loaded set BEFORE keyword filtering — the window the
        // footer's match count is measured against.
        items,
        visibleItems: records,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchNextPage,
    } = useSteps(filters);

    // Same module-scoped store the list hook filters on — this component owns
    // the input, useSteps owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = useStepsSearch();

    const {
        selection,
        handleRowSelection,
        handleClearSelection
    } = useStepsSelection();

    // Checkboxes appear only for an operator who can actually delete. Offering
    // selection to someone with no action to apply to it is a dead affordance.
    let selectedRecords: Step[] = [];
    if (allowedActions.includes(rbacActions.DeleteStep)) {
        selectedRecords = records.filter(record => selection[record.id] === true)
    }

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Two distinct empty states: no steps defined for this phase yet, versus
    // nothing matched the search. Collapsing them into one message tells an
    // operator who mistyped a keyword that the phase has no steps at all.
    const noRecordsText = keyword
        ? `No steps match "${keyword}" in the records loaded so far.`
        : 'No steps in this phase yet.';

    return (
        <Stack>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title='Could not load steps'>
                    The workflows API did not return the list of steps. Try Reload; if it
                    persists the API may be unavailable in this environment.
                </Alert>
            )}

            <MantineDataTable
                columns={columns as DataTableColumn[]}
                records={records}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    items.forEach((item: any) => {
                        handleRowSelection(item.id, true)
                    })
                }}
                // Sorting is client-side over the pages loaded so far — the same
                // scope the keyword filter works on. Token pagination cannot
                // reorder rows the browser has not fetched. The default is
                // `position` ascending, which is the step SEQUENCE and matches
                // the order the API returns, so the loaded window is an ordered
                // prefix rather than a reshuffled sample.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchNextPage()}
                scrollViewportRef={scrollViewportRef}
                onRowClick={({record}) => {
                    const step = record as Step;
                    navigate(`/playbooks/${playbookId ?? step.playbookId}/phases/${phaseId ?? step.phaseId}/steps/${encodeURIComponent(step.id)}`);
                }}
            />

            <DataTableTokenPagination
                loadedCount={records.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                isFetchingMore={isFetchingNextPage}
                nextToken={nextToken}
                handleLoadMore={fetchNextPage}
                noun='step'
            />
        </Stack>
    );
};
